import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    IconButton,
    Typography,
    SelectChangeEvent,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Close as CloseIcon } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { ThemeStyle } from '../../../../module/ThemeManager';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { IContest } from '../../../../types/global/helper';
import { isEmpty } from '../../../../utils/lodash';
import {
    brandAwardBodyTextSx,
    brandContestDialogFooterSx,
    brandContestDialogPaperSx,
    editorialHeadlineSx,
    editorialInputSx,
    editorialLabelSx,
    editorialSelectMenuProps,
    midnight,
} from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';
import {
    awardPrizeCentsToFormDollars,
    awardPrizeFormDollarsToApiValue,
} from '../../../../pages/BrandDashboard/contestWizard/buildAwardCategoryPayload';

interface CreateAwardCategoryDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    contest: IContest;
    editAward?: any;
    visualVariant?: 'default' | 'brand';
}

function hasCategories(contest: IContest) {
    return (
        Array.isArray(contest.categories) &&
        contest.categories.length > 0 &&
        contest.categories.some(cat => cat && Object.keys(cat).length > 0 && cat.category_name)
    );
}

function legacyTextFieldSx(themeStyle: ThemeStyle) {
    return {
        '& .MuiInputLabel-root': {
            color: theme[themeStyle].primary_white,
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
        },
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme[themeStyle].gray_text,
            color: theme[themeStyle].primary_white,
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            '& fieldset': { borderColor: theme[themeStyle].primary_white },
        },
        '& .MuiInputBase-input': {
            color: theme[themeStyle].primary_white,
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
        },
    };
}

function legacySelectSx(themeStyle: ThemeStyle) {
    return {
        color: theme[themeStyle].primary_white,
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        background: theme[themeStyle].gray_text,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme[themeStyle].primary_white,
        },
        '& .MuiSvgIcon-root': {
            color: theme[themeStyle].primary_white,
        },
    };
}

function legacyMenuProps(themeStyle: ThemeStyle) {
    return {
        PaperProps: {
            sx: {
                backgroundColor: theme[themeStyle].form_black,
                color: theme[themeStyle].primary_white,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
            },
        },
    };
}

function legacyMenuItemSx(themeStyle: ThemeStyle) {
    return {
        color: theme[themeStyle].primary_white,
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        backgroundColor: theme[themeStyle].form_black,
        '&:hover': { backgroundColor: theme[themeStyle].highlight },
    };
}

type AwardFormSelectProps = {
    label: string;
    value: string;
    onChange: (value: string) => void;
    required?: boolean;
    isBrand: boolean;
    themeStyle: ThemeStyle;
    children: React.ReactNode;
};

function AwardFormSelect({ label, value, onChange, required, isBrand, themeStyle, children }: AwardFormSelectProps) {
    return (
        <FormControl fullWidth required={required} sx={isBrand ? editorialInputSx : { backgroundColor: 'transparent' }}>
            <InputLabel
                sx={
                    isBrand
                        ? { color: midnight.onSurface }
                        : { color: theme[themeStyle].primary_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }
                }>
                {label}
            </InputLabel>
            <Select
                value={value}
                onChange={(e: SelectChangeEvent<string>) => onChange(e.target.value)}
                label={label}
                sx={isBrand ? editorialInputSx : legacySelectSx(themeStyle)}
                MenuProps={isBrand ? editorialSelectMenuProps : legacyMenuProps(themeStyle)}
                {...(isBrand
                    ? {}
                    : {
                          IconComponent: (props: object) => (
                              <ArrowDropDownIcon {...props} sx={{ color: theme[themeStyle].primary_white }} />
                          ),
                      })}>
                {children}
            </Select>
        </FormControl>
    );
}

const CreateAwardCategoryDialog: React.FC<CreateAwardCategoryDialogProps> = ({
    open,
    onClose,
    onSubmit,
    contest,
    editAward,
    visualVariant = 'default',
}) => {
    const { themeStyle } = useThemeContext();
    const isBrand = visualVariant === 'brand';
    const [awardData, setAwardData] = useState({
        category_title: '',
        prize: '',
        additional_prize: [''],
        sub_category: '',
        contest: contest._id,
        category: '',
        position: '',
        tier_label: '',
    });
    const prevCategoryRef = useRef<string>('');

    const selectedCategory = contest.categories?.find(cat => cat._id === awardData.category);
    const hasTieredPricing = selectedCategory?.with_tiered_pricing === true;
    const availablePositions = useMemo(() => {
        const positions = new Set(
            (selectedCategory?.prize_structure ?? [])
                .map(prize => prize.position?.trim())
                .filter((position): position is string => Boolean(position)),
        );
        const fromEdit = editAward?.tier_position || editAward?.position;
        if (typeof fromEdit === 'string' && fromEdit.trim()) {
            positions.add(fromEdit.trim());
        }
        if (awardData.position?.trim()) {
            positions.add(awardData.position.trim());
        }
        return Array.from(positions);
    }, [selectedCategory?.prize_structure, editAward, awardData.position]);
    const availableTiers = selectedCategory?.tier_fee || [];

    const hasEmptyFields =
        (!hasTieredPricing && isEmpty(awardData.category_title)) ||
        !(awardData.prize || awardData.additional_prize) ||
        isEmpty(awardData.category) ||
        (hasTieredPricing && (isEmpty(awardData.position) || isEmpty(awardData.tier_label)));

    const dialogTitle = editAward?._id ? 'Edit award category' : 'Create award category';

    const paperProps = isBrand
        ? { sx: brandContestDialogPaperSx }
        : {
              sx: {
                  backgroundColor: theme[themeStyle].form_black,
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                  color: theme[themeStyle].primary_white,
              },
          };

    const emptyPaperProps = isBrand
        ? { sx: brandContestDialogPaperSx }
        : {
              sx: {
                  backgroundColor: theme[themeStyle].modal_dark,
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.9)',
                  WebkitTextFillColor: theme[themeStyle].true_white,
                  color: theme[themeStyle].true_white,
              },
          };

    useEffect(() => {
        if (editAward) {
            const categoryIdFromAward =
                typeof editAward.category === 'string' ? editAward.category : editAward.category?._id || '';

            let selectedCategoryForEdit = contest.categories?.find(cat => cat._id === categoryIdFromAward);
            if (!selectedCategoryForEdit && editAward._id) {
                selectedCategoryForEdit = contest.categories?.find(
                    cat =>
                        Array.isArray(cat.category_awards) &&
                        cat.category_awards.some(
                            (linked: any) => (typeof linked === 'string' ? linked : linked?._id) === editAward._id,
                        ),
                );
            }

            const categoryId = selectedCategoryForEdit?._id || categoryIdFromAward || '';
            const isTieredForEdit = selectedCategoryForEdit?.with_tiered_pricing === true;

            let tierLabel = (editAward.tier_label || '').trim();
            if (!tierLabel && editAward.tier_name && selectedCategoryForEdit?.tier_fee) {
                const matchedTier = selectedCategoryForEdit.tier_fee.find(
                    (tier: { _id?: string; label?: string }) =>
                        tier._id === editAward.tier_name || tier.label === editAward.tier_name,
                );
                if (matchedTier?.label) {
                    tierLabel = matchedTier.label.trim();
                } else if (editAward.tier_name && !/^[a-f0-9]{24}$/i.test(editAward.tier_name)) {
                    tierLabel = editAward.tier_name.trim();
                }
            }

            let position = (editAward.position || editAward.tier_position || '').trim();
            if (!position && isTieredForEdit && selectedCategoryForEdit?.prize_structure) {
                for (const prize of selectedCategoryForEdit.prize_structure) {
                    const hasPayout = (prize.payouts || []).some(
                        (payout: any) => (typeof payout === 'string' ? payout : payout?._id) === editAward._id,
                    );
                    if (hasPayout) {
                        position = (prize.position || '').trim();
                        break;
                    }
                }
            }

            const categoryTitle =
                isTieredForEdit && (position || editAward.tier_position || editAward.position)
                    ? position || editAward.tier_position || editAward.position
                    : editAward.category_title;

            setAwardData({
                category_title: categoryTitle,
                prize: awardPrizeCentsToFormDollars(editAward.prize),
                additional_prize: editAward.additional_prize || [''],
                sub_category: editAward.sub_category || '',
                contest: contest._id,
                category: categoryId,
                position: position || editAward.position || editAward.tier_position || '',
                tier_label: tierLabel,
            });
        } else {
            setAwardData({
                category_title: '',
                prize: '',
                additional_prize: [''],
                sub_category: '',
                contest: contest._id,
                category: '',
                position: '',
                tier_label: '',
            });
        }
    }, [editAward, contest._id, contest.categories]);

    useEffect(() => {
        if (!editAward && awardData.category !== prevCategoryRef.current && prevCategoryRef.current !== '') {
            setAwardData(prev => ({
                ...prev,
                position: '',
                tier_label: '',
                category_title: '',
            }));
        }
        prevCategoryRef.current = awardData.category;
    }, [awardData.category, editAward]);

    useEffect(() => {
        if (hasTieredPricing && awardData.position) {
            setAwardData(prev => ({
                ...prev,
                category_title: awardData.position,
            }));
        }
    }, [awardData.position, hasTieredPricing]);

    const handleChange = (field: string, value: string) => {
        setAwardData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleAdditionalPrizeChange = (index: number, value: string) => {
        const newAdditionalPrizes = [...awardData.additional_prize];
        newAdditionalPrizes[index] = value;
        setAwardData(prev => ({
            ...prev,
            additional_prize: newAdditionalPrizes,
        }));
    };

    const addAdditionalPrize = () => {
        setAwardData(prev => ({
            ...prev,
            additional_prize: [...prev.additional_prize, ''],
        }));
    };

    const removeAdditionalPrize = (index: number) => {
        const newAdditionalPrizes = awardData.additional_prize.filter((_, i) => i !== index);
        setAwardData(prev => ({
            ...prev,
            additional_prize: newAdditionalPrizes,
        }));
    };

    const handleSubmit = () => {
        const submitData: any = {
            ...awardData,
            prize: awardPrizeFormDollarsToApiValue(awardData.prize),
            additional_prize: awardData.additional_prize.filter(prize => prize.trim() !== ''),
        };

        if (hasTieredPricing) {
            submitData.position = awardData.position;
            submitData.tier_label = awardData.tier_label;
            // Backend stores tier_fee ObjectId on tier_name — never send the display label.
            const matchedTier = availableTiers.find(
                (tier: { label?: string; _id?: string }) => tier.label === awardData.tier_label,
            );
            submitData.tier_name = matchedTier?._id || editAward?.tier_name || awardData.tier_label;
            submitData.tier_position = awardData.position;
            submitData.category_title = awardData.position;
        }

        onSubmit(submitData);
    };

    const formatNumberWithCommas = (value: string | number) => {
        if (value === '' || value === null || value === undefined) return '';
        const numStr = String(value).replace(/,/g, '');
        if (numStr === '' || numStr === '.') return numStr;
        const num = parseFloat(numStr);
        if (isNaN(num)) return String(value);
        return num.toLocaleString('en-US', {
            maximumFractionDigits: 20,
            useGrouping: true,
        });
    };

    const renderDialogHeader = (title: string) => {
        if (isBrand) {
            return (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1.5, pb: 0 }}>
                        <IconButton onClick={onClose} aria-label="Close" sx={{ color: midnight.onSurfaceVariant }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ px: { xs: 2, md: 3 }, pt: 0, pb: 1 }}>
                        <Typography
                            sx={{
                                ...editorialHeadlineSx,
                                fontSize: { xs: 24, sm: 28 },
                                color: midnight.onSurface,
                                textTransform: 'capitalize',
                            }}>
                            {title}
                        </Typography>
                    </Box>
                </>
            );
        }
        return (
            <DialogTitle
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    color: theme[themeStyle].primary_white,
                    backgroundColor: theme[themeStyle].form_black,
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                }}>
                {title}
            </DialogTitle>
        );
    };

    const renderDialogActions = (primaryLabel: string, onPrimary: () => void, primaryDisabled?: boolean) => {
        if (isBrand) {
            return (
                <DialogActions sx={brandContestDialogFooterSx}>
                    <Button
                        onClick={onClose}
                        sx={{ color: midnight.onSurfaceVariant, fontWeight: 700, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onPrimary}
                        disabled={primaryDisabled}
                        variant="contained"
                        sx={{
                            bgcolor: midnight.primary,
                            color: '#392700',
                            fontWeight: 800,
                            px: 4,
                            py: 1.25,
                            borderRadius: '12px',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#e6b55a' },
                            '&.Mui-disabled': {
                                bgcolor: 'rgba(255,201,101,0.35)',
                                color: 'rgba(57,39,0,0.5)',
                            },
                        }}>
                        {primaryLabel}
                    </Button>
                </DialogActions>
            );
        }
        return (
            <DialogActions sx={{ backgroundColor: theme[themeStyle].form_black }}>
                <Button
                    onClick={onClose}
                    sx={{ color: theme[themeStyle].primary_white, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    Cancel
                </Button>
                <Button
                    onClick={onPrimary}
                    disabled={primaryDisabled}
                    variant="contained"
                    sx={{
                        backgroundColor: theme[themeStyle].highlight,
                        color: theme[themeStyle].black,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].primary_orange,
                        },
                    }}>
                    {primaryLabel}
                </Button>
            </DialogActions>
        );
    };

    const contentSx = isBrand
        ? { px: { xs: 2, md: 3 }, pt: 0, pb: 2 }
        : { backgroundColor: theme[themeStyle].form_black };

    const fieldGap = isBrand ? 2.5 : 2;

    if (!hasCategories(contest)) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={emptyPaperProps}>
                {isBrand ? (
                    renderDialogHeader('Create award category')
                ) : (
                    <DialogTitle sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: theme[themeStyle].tag_white }}>
                        Create Award Category
                    </DialogTitle>
                )}
                <DialogContent sx={contentSx}>
                    <Typography
                        sx={
                            isBrand
                                ? { ...brandAwardBodyTextSx, fontSize: 14, lineHeight: 1.65 }
                                : { color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }
                        }>
                        Please create submission categories first before creating award categories. Navigate to Details
                        and add at least one submission category.
                    </Typography>
                </DialogContent>
                {isBrand ? (
                    <DialogActions sx={brandContestDialogFooterSx}>
                        <Button
                            onClick={onClose}
                            variant="contained"
                            sx={{
                                bgcolor: midnight.primary,
                                color: '#392700',
                                fontWeight: 800,
                                px: 4,
                                borderRadius: '12px',
                                textTransform: 'none',
                                '&:hover': { bgcolor: '#e6b55a' },
                            }}>
                            Close
                        </Button>
                    </DialogActions>
                ) : (
                    <DialogActions>
                        <Button
                            onClick={onClose}
                            sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            Close
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" PaperProps={paperProps}>
            {renderDialogHeader(dialogTitle)}
            <DialogContent sx={contentSx}>
                <Box sx={{ mt: isBrand ? 1 : 2, display: 'flex', flexDirection: 'column', gap: fieldGap }}>
                    <AwardFormSelect
                        label="Category"
                        value={awardData.category}
                        onChange={v => handleChange('category', v)}
                        required
                        isBrand={isBrand}
                        themeStyle={themeStyle}>
                        {contest.categories?.map((category, index) => (
                            <MenuItem
                                key={category._id ?? index}
                                value={category._id}
                                sx={isBrand ? undefined : legacyMenuItemSx(themeStyle)}>
                                {category.category_name}
                            </MenuItem>
                        ))}
                    </AwardFormSelect>

                    {!hasTieredPricing && (
                        <TextField
                            label="Award name"
                            value={awardData.category_title}
                            onChange={e => handleChange('category_title', e.target.value)}
                            fullWidth
                            required
                            sx={isBrand ? editorialInputSx : legacyTextFieldSx(themeStyle)}
                        />
                    )}

                    {hasTieredPricing && (
                        <>
                            <AwardFormSelect
                                label="Tier"
                                value={awardData.tier_label}
                                onChange={v => handleChange('tier_label', v)}
                                required
                                isBrand={isBrand}
                                themeStyle={themeStyle}>
                                {availableTiers.length > 0 ? (
                                    availableTiers.map((tier: { label: string }, index: number) => (
                                        <MenuItem
                                            key={index}
                                            value={tier.label}
                                            sx={isBrand ? undefined : legacyMenuItemSx(themeStyle)}>
                                            {tier.label}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem
                                        disabled
                                        sx={
                                            isBrand
                                                ? { color: midnight.onSurfaceVariant }
                                                : legacyMenuItemSx(themeStyle)
                                        }>
                                        No tiers available. Add tiers in Details.
                                    </MenuItem>
                                )}
                            </AwardFormSelect>
                            <AwardFormSelect
                                label="Position"
                                value={awardData.position}
                                onChange={v => handleChange('position', v)}
                                required
                                isBrand={isBrand}
                                themeStyle={themeStyle}>
                                {availablePositions.length > 0 ? (
                                    availablePositions.map((position, index) => (
                                        <MenuItem
                                            key={index}
                                            value={position}
                                            sx={isBrand ? undefined : legacyMenuItemSx(themeStyle)}>
                                            {position}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem
                                        disabled
                                        sx={
                                            isBrand
                                                ? { color: midnight.onSurfaceVariant }
                                                : legacyMenuItemSx(themeStyle)
                                        }>
                                        No positions available. Add placements in Details.
                                    </MenuItem>
                                )}
                            </AwardFormSelect>
                        </>
                    )}

                    <TextField
                        label="Prize amount (USD)"
                        type="text"
                        value={awardData.prize === '' ? '' : formatNumberWithCommas(awardData.prize)}
                        onChange={e => {
                            const inputValue = e.target.value;
                            let raw = inputValue.replace(/,/g, '').replace(/[^\d.]/g, '');
                            const parts = raw.split('.');
                            if (parts.length > 2) {
                                raw = parts[0] + '.' + parts.slice(1).join('');
                            }
                            handleChange('prize', raw);
                        }}
                        onBlur={e => {
                            const raw = e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '');
                            if (raw === '' || raw === '.') {
                                handleChange('prize', '');
                            } else {
                                const num = parseFloat(raw);
                                if (!isNaN(num)) {
                                    handleChange('prize', String(num));
                                }
                            }
                        }}
                        fullWidth
                        required
                        InputProps={{
                            startAdornment: (
                                <Typography
                                    sx={
                                        isBrand
                                            ? { color: midnight.onSurfaceVariant, fontWeight: 600, mr: 0.5 }
                                            : {
                                                  color: theme[themeStyle].primary_white,
                                                  fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                              }
                                    }>
                                    $
                                </Typography>
                            ),
                        }}
                        sx={isBrand ? editorialInputSx : legacyTextFieldSx(themeStyle)}
                    />

                    <Typography
                        sx={
                            isBrand
                                ? { ...brandAwardBodyTextSx, fontSize: 12 }
                                : {
                                      color: theme[themeStyle].primary_white,
                                      fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                      marginTop: 1,
                                      marginBottom: 1,
                                  }
                        }>
                        Note: The prize will be shown to users in their local currency.
                    </Typography>

                    <AwardFormSelect
                        label="Sub category"
                        value={awardData.sub_category}
                        onChange={v => handleChange('sub_category', v)}
                        isBrand={isBrand}
                        themeStyle={themeStyle}>
                        {(['male', 'female', 'other'] as const).map(value => (
                            <MenuItem key={value} value={value} sx={isBrand ? undefined : legacyMenuItemSx(themeStyle)}>
                                {value.charAt(0).toUpperCase() + value.slice(1)}
                            </MenuItem>
                        ))}
                    </AwardFormSelect>

                    <Box>
                        <Typography
                            sx={
                                isBrand
                                    ? { ...editorialLabelSx, letterSpacing: '0.14em', mb: 1.5, display: 'block' }
                                    : {
                                          color: theme[themeStyle].primary_white,
                                          mb: 1,
                                          fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                      }
                            }>
                            Additional prizes
                        </Typography>
                        {awardData.additional_prize.map((prize, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                                <TextField
                                    value={prize}
                                    onChange={e => handleAdditionalPrizeChange(index, e.target.value)}
                                    fullWidth
                                    placeholder="e.g. Certificate, Trophy"
                                    sx={isBrand ? editorialInputSx : legacyTextFieldSx(themeStyle)}
                                />
                                <IconButton
                                    onClick={() => removeAdditionalPrize(index)}
                                    size="small"
                                    sx={{
                                        mt: 0.5,
                                        color: isBrand ? midnight.onSurfaceVariant : theme[themeStyle].primary_white,
                                        '&:hover': {
                                            color: isBrand ? midnight.primary : theme[themeStyle].highlight,
                                        },
                                    }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        <Button
                            startIcon={<AddIcon />}
                            onClick={addAdditionalPrize}
                            sx={
                                isBrand
                                    ? {
                                          color: midnight.secondary,
                                          fontWeight: 700,
                                          fontSize: 13,
                                          textTransform: 'none',
                                          mt: 0.5,
                                          '&:hover': { bgcolor: 'transparent', opacity: 0.85 },
                                      }
                                    : { color: theme[themeStyle].primary_white, fontFamily: FONT_ENUM.MAIN_BOLD }
                            }>
                            Add prize
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
            {renderDialogActions(editAward ? 'Update' : 'Create', handleSubmit, hasEmptyFields)}
        </Dialog>
    );
};

export default CreateAwardCategoryDialog;
